(() => {
  'use strict';

  const OPTION_NAME = 'size';
  const FIELD_CLASS = 'nomad-size-variant-field--pills';
  const ENHANCED_ATTR = 'data-nomad-size-pill-enhanced';

  let scanQueued = false;

  const normalizeText = (value = '') =>
    String(value)
      .replace(/\s+/g, ' ')
      .replace(/:\s*$/, '')
      .trim()
      .toLowerCase();

  const getFieldName = (field) => {
    const legend =
      field.querySelector(':scope > legend.form__label') ||
      field.querySelector(':scope > legend') ||
      field.querySelector(':scope > .form__label');

    return normalizeText(legend?.textContent || '');
  };

  const getRadioInputs = (field) =>
    Array.from(
      field.querySelectorAll(':scope > input[type="radio"]')
    );

  const getLabelForRadio = (field, radio) => {
    if (!radio?.id) return null;

    try {
      return field.querySelector(
        `:scope > label[for="${CSS.escape(radio.id)}"]`
      );
    } catch (error) {
      return null;
    }
  };

  const looksUnavailable = (radio, label) => {
    if (!radio) return true;

    return Boolean(
      radio.disabled ||
      radio.getAttribute('aria-disabled') === 'true' ||
      radio.classList.contains('disabled') ||
      radio.classList.contains('is-disabled') ||
      label?.getAttribute('aria-disabled') === 'true' ||
      label?.classList.contains('disabled') ||
      label?.classList.contains('is-disabled') ||
      label?.classList.contains('soldout') ||
      label?.classList.contains('sold-out') ||
      label?.querySelector('.label-unavailable')
    );
  };

  class NomadSizePillPicker {
    constructor(field) {
      this.field = field;
      this.radios = [];
      this.nativeLabels = [];
      this.isOpen = false;
      this.destroyed = false;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
      this.onOptionsClick = this.onOptionsClick.bind(this);
      this.onOptionsKeydown = this.onOptionsKeydown.bind(this);
      this.onNativeChange = this.onNativeChange.bind(this);
      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

      this.refreshNativeReferences();

      if (!this.radios.length) return;

      this.build();
      this.bind();
      this.sync();
    }

    refreshNativeReferences() {
      this.radios = getRadioInputs(this.field);
      this.nativeLabels = this.radios
        .map((radio) => getLabelForRadio(this.field, radio))
        .filter(Boolean);

      this.nativeLabels.forEach((label) => {
        label.setAttribute('data-nomad-size-native-label', '');
      });
    }

    build() {
      this.field.setAttribute(ENHANCED_ATTR, 'true');
      this.field.classList.add(FIELD_CLASS);

      this.root = document.createElement('div');
      this.root.className = 'nomad-size-picker';
      this.root.setAttribute('data-nomad-size-picker', '');
      this.root.setAttribute('data-picker-source', 'pills');

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'nomad-size-picker__trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');

      this.value = document.createElement('span');
      this.value.className = 'nomad-size-picker__value';

      this.caret = document.createElement('span');
      this.caret.className = 'nomad-size-picker__caret';
      this.caret.setAttribute('aria-hidden', 'true');

      this.trigger.append(this.value, this.caret);

      this.menu = document.createElement('div');
      this.menu.className = 'nomad-size-picker__menu';

      this.options = document.createElement('div');
      this.options.className = 'nomad-size-picker__options';
      this.options.setAttribute('role', 'listbox');
      this.options.tabIndex = -1;

      this.menu.append(this.options);
      this.root.append(this.trigger, this.menu);
      this.field.append(this.root);

      this.renderOptions();
    }

    bind() {
      this.trigger.addEventListener('click', this.onTriggerClick);
      this.trigger.addEventListener('keydown', this.onTriggerKeydown);
      this.options.addEventListener('click', this.onOptionsClick);
      this.options.addEventListener('keydown', this.onOptionsKeydown);
      this.field.addEventListener('change', this.onNativeChange);

      document.addEventListener('pointerdown', this.onDocumentPointer);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      document.removeEventListener(
        'pointerdown',
        this.onDocumentPointer
      );
      document.removeEventListener(
        'keydown',
        this.onDocumentKeydown
      );
    }

    getSelectedRadio() {
      this.refreshNativeReferences();

      return (
        this.radios.find((radio) => radio.checked) ||
        this.radios[0] ||
        null
      );
    }

    getDisplayText(radio) {
      if (!radio) return 'Select size';

      const label = getLabelForRadio(this.field, radio);
      const cloned = label?.cloneNode(true);

      cloned
        ?.querySelectorAll(
          '.visually-hidden, .label-unavailable, svg'
        )
        .forEach((node) => node.remove());

      const labelText = cloned?.textContent?.trim();

      return labelText || radio.value || 'Select size';
    }

    renderOptions() {
      if (!this.options) return;

      this.refreshNativeReferences();
      this.options.replaceChildren();

      this.radios.forEach((radio, index) => {
        const label = getLabelForRadio(this.field, radio);
        const unavailable = looksUnavailable(radio, label);

        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'nomad-size-picker__option';
        option.dataset.radioId = radio.id || '';
        option.dataset.index = String(index);
        option.textContent = this.getDisplayText(radio);
        option.setAttribute('role', 'option');
        option.setAttribute(
          'aria-selected',
          radio.checked ? 'true' : 'false'
        );
        option.setAttribute(
          'aria-disabled',
          unavailable ? 'true' : 'false'
        );
        option.disabled = unavailable;

        this.options.append(option);
      });
    }

    sync() {
      if (!this.root?.isConnected) {
        this.destroy();
        return;
      }

      this.refreshNativeReferences();

      const selected = this.getSelectedRadio();

      this.value.textContent = this.getDisplayText(selected);

      const optionButtons = this.getOptionButtons();

      optionButtons.forEach((button) => {
        const radio = this.getRadioByButton(button);
        const label = radio
          ? getLabelForRadio(this.field, radio)
          : null;
        const unavailable = looksUnavailable(radio, label);

        button.textContent = this.getDisplayText(radio);
        button.disabled = unavailable;
        button.setAttribute(
          'aria-disabled',
          unavailable ? 'true' : 'false'
        );
        button.setAttribute(
          'aria-selected',
          radio?.checked ? 'true' : 'false'
        );
      });
    }

    getOptionButtons() {
      return Array.from(
        this.options.querySelectorAll('.nomad-size-picker__option')
      );
    }

    getRadioByButton(button) {
      const id = button?.dataset?.radioId;
      if (!id) return null;

      try {
        return this.field.querySelector(
          `:scope > input[type="radio"]#${CSS.escape(id)}`
        );
      } catch (error) {
        return null;
      }
    }

    open({ focusSelected = false } = {}) {
      if (this.isOpen || !this.root?.isConnected) return;

      /*
       * Another option such as Color can change Size availability.
       * Rebuild from the live native pills each time the dropdown opens.
       */
      this.renderOptions();
      this.sync();

      this.isOpen = true;
      this.root.classList.add('is-open');
      this.field.classList.add('is-size-picker-open');
      this.trigger.setAttribute('aria-expanded', 'true');

      if (focusSelected) {
        requestAnimationFrame(() => {
          const buttons = this.getOptionButtons();
          const target =
            buttons.find(
              (button) =>
                button.getAttribute('aria-selected') === 'true' &&
                !button.disabled
            ) ||
            buttons.find((button) => !button.disabled);

          target?.focus();
        });
      }
    }

    close({ restoreFocus = false } = {}) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.root.classList.remove('is-open');
      this.field.classList.remove('is-size-picker-open');
      this.trigger.setAttribute('aria-expanded', 'false');

      if (restoreFocus && this.trigger.isConnected) {
        this.trigger.focus();
      }
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    choose(button) {
      const radio = this.getRadioByButton(button);
      const label = radio
        ? getLabelForRadio(this.field, radio)
        : null;

      if (!radio || looksUnavailable(radio, label)) return;

      this.close();

      if (!radio.checked) {
        /*
         * Programmatic click uses the exact native radio Dawn already listens
         * to. This preserves variant URL, price, media, gallery and availability
         * behavior without duplicating Shopify's variant logic.
         */
        radio.click();
      }

      requestAnimationFrame(() => {
        this.sync();
        this.trigger?.focus();
      });
    }

    moveFocus(direction) {
      const enabled = this.getOptionButtons().filter(
        (button) => !button.disabled
      );

      if (!enabled.length) return;

      let index = enabled.indexOf(document.activeElement);

      if (index < 0) {
        index = enabled.findIndex(
          (button) =>
            button.getAttribute('aria-selected') === 'true'
        );
      }

      if (index < 0) index = 0;

      const next =
        (index + direction + enabled.length) % enabled.length;

      enabled[next]?.focus();
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.toggle();
    }

    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.open({ focusSelected: true });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.open({ focusSelected: true });

        requestAnimationFrame(() => this.moveFocus(-1));
      }
    }

    onOptionsClick(event) {
      const button = event.target.closest(
        '.nomad-size-picker__option'
      );

      if (button) this.choose(button);
    }

    onOptionsKeydown(event) {
      const button = event.target.closest(
        '.nomad-size-picker__option'
      );

      if (!button) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveFocus(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveFocus(-1);
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.choose(button);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ restoreFocus: true });
      }
    }

    onNativeChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;

      /*
       * Allow Dawn's own change handler to run first. Its variant update can
       * replace this whole fieldset, in which case the global scan enhances
       * the newly rendered Size field automatically.
       */
      requestAnimationFrame(() => this.sync());
    }

    onDocumentPointer(event) {
      if (
        this.isOpen &&
        this.root?.isConnected &&
        !this.root.contains(event.target)
      ) {
        this.close();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close({ restoreFocus: true });
      }
    }
  }

  const isSizePillField = (field) => {
    if (!(field instanceof HTMLElement)) return false;
    if (getFieldName(field) !== OPTION_NAME) return false;

    return getRadioInputs(field).length > 0;
  };

  const enhanceField = (field) => {
    if (
      !isSizePillField(field) ||
      field.hasAttribute(ENHANCED_ATTR)
    ) {
      return;
    }

    const picker = new NomadSizePillPicker(field);

    if (picker.root) {
      field.nomadSizePillPicker = picker;
    }
  };

  const scan = (root = document) => {
    const fields = root.matches?.('.product-form__input')
      ? [root]
      : Array.from(
          root.querySelectorAll?.('.product-form__input') || []
        );

    fields.forEach(enhanceField);

    /*
     * Also refresh already enhanced Size fields because Color changes may alter
     * which Size radios Dawn marks unavailable without replacing the fieldset.
     */
    document
      .querySelectorAll(
        `.product-form__input.${FIELD_CLASS}[${ENHANCED_ATTR}]`
      )
      .forEach((field) => {
        field.nomadSizePillPicker?.sync?.();
      });
  };

  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;

    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  };

  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      if (mutation.type === 'childList') {
        return mutation.addedNodes.length > 0;
      }

      if (mutation.type !== 'attributes') return false;

      const target = mutation.target;

      if (
        !(target instanceof HTMLElement) ||
        target.closest('[data-nomad-size-picker]')
      ) {
        return false;
      }

      return (
        target.matches('input[type="radio"]') ||
        target.matches('label[for]')
      );
    });

    if (relevant) queueScan();
  });

  const start = () => {
    scan();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'class',
        'disabled',
        'aria-disabled',
        'checked',
      ],
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        queueScan();
      }
    });

    document.addEventListener(
      'shopify:section:load',
      queueScan
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true,
    });
  } else {
    start();
  }
})();
